import PropTypes from 'prop-types'
import {
  UserIcon,
  MonitorIcon,
  GraduationCapIcon,
  CheckIcon,
  XIcon,
} from '../../../../assets/svg'
import { ORGANIZER_REQUEST_STATUSES } from '../../../../assets/const'

const Chip = ({ children }) => (
  <span className="rounded-[50px] border border-solid border-[#e5e7eb] bg-white px-[0.875rem] sm:px-[1rem] py-[0.375rem] text-[0.812rem] sm:text-[0.875rem] text-[#475569] break-words">
    {children}
  </span>
)

Chip.propTypes = { children: PropTypes.node.isRequired }

const SectionLabel = ({ icon, children }) => (
  <div className="flex items-center gap-[0.5rem] text-[0.812rem] sm:text-[0.875rem] text-[#9ca3af]">
    {icon}
    <span>{children}</span>
  </div>
)

SectionLabel.propTypes = {
  icon: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
}

const STATUS_BADGE = {
  [ORGANIZER_REQUEST_STATUSES.APPROVED]: {
    label: 'Одобрено',
    bg: '#D1FADA',
    color: '#228B51',
  },
  [ORGANIZER_REQUEST_STATUSES.REJECTED]: {
    label: 'Отклонено',
    bg: '#FEE2E2',
    color: '#B91C1C',
  },
}

const StatusBadge = ({ status }) => {
  const meta = STATUS_BADGE[status]
  if (!meta) return null
  return (
    <span
      className="flex items-center gap-[0.375rem] whitespace-nowrap rounded-[50px] px-[0.75rem] sm:px-[0.875rem] py-[0.375rem] text-[0.812rem] sm:text-[0.875rem] font-medium"
      style={{ backgroundColor: meta.bg, color: meta.color }}
    >
      {meta.label}
    </span>
  )
}

StatusBadge.propTypes = {
  status: PropTypes.oneOf(Object.values(ORGANIZER_REQUEST_STATUSES)).isRequired,
}

const RequestCard = ({
  number,
  projectName,
  institution,
  participants,
  supervisors,
  equipment,
  submittedAt,
  status,
  onApprove,
  onReject,
}) => {
  const isNew = status === ORGANIZER_REQUEST_STATUSES.NEW

  return (
    <article className="flex w-full flex-col gap-[1rem] sm:gap-[1.25rem] rounded-[20px] border border-solid border-[#e5e7eb] bg-white p-[1.25rem] sm:p-[1.5rem] lg:p-[1.75rem]">
      <div className="flex flex-col gap-[0.75rem] sm:flex-row sm:items-start sm:justify-between sm:gap-[1.25rem]">
        <div className="flex flex-col gap-[0.5rem] min-w-0">
          <span className="text-[0.812rem] sm:text-[0.875rem] text-[#9ca3af]">Заявка №{number}</span>
          <h3 className="m-0 text-[1.125rem] sm:text-[1.25rem] font-semibold leading-[1.4] text-[#111827] break-words">
            {projectName}
          </h3>
        </div>
        <div className="self-start">
          <StatusBadge status={status} />
        </div>
      </div>

      <div className="flex flex-col gap-[0.375rem]">
        <SectionLabel icon={<GraduationCapIcon />}>Образовательное учреждение</SectionLabel>
        <div className="text-[0.875rem] sm:text-[0.938rem] text-[#111827] break-words">{institution}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[1.5rem] gap-y-[0.625rem]">
        <SectionLabel icon={<UserIcon />}>Участники</SectionLabel>
        <SectionLabel icon={<UserIcon />}>Руководители</SectionLabel>

        <div className="flex flex-col items-start gap-[0.5rem]">
          {participants.map((p, i) => (
            <Chip key={i}>
              {p.name} ({p.code})
            </Chip>
          ))}
        </div>

        <div className="flex flex-col items-start gap-[0.5rem]">
          {supervisors.map((s, i) => (
            <Chip key={i}>
              {s.name}{' '}
              <span className="text-[#9ca3af]">({s.position})</span>{' '}
              <span className="text-[#9ca3af]">(Телефон: {s.phone})</span>
            </Chip>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-[0.375rem]">
        <SectionLabel icon={<MonitorIcon />}>Необходимое оборудование</SectionLabel>
        <div className="text-[0.875rem] sm:text-[0.938rem] text-[#111827] break-words">{equipment}</div>
      </div>

      <div className="flex flex-col gap-[0.75rem] sm:flex-row sm:items-center sm:justify-between sm:gap-[1rem] pt-[0.25rem]">
        <span className="text-[0.812rem] sm:text-[0.875rem] text-[#9ca3af]">
          Дата подачи заявки: {submittedAt}
        </span>
        {isNew && (
          <div className="flex flex-wrap items-center gap-[0.625rem]">
            <button
              type="button"
              onClick={onReject}
              className="flex flex-1 sm:flex-none items-center justify-center gap-[0.5rem] rounded-[10px] border-0 bg-[#1F2937] px-[1rem] sm:px-[1.25rem] py-[0.625rem] sm:py-[0.75rem] text-[0.875rem] sm:text-[0.938rem] font-medium text-white hover:bg-[#111827] cursor-pointer"
            >
              <XIcon color="#fff" size={16} />
              Отклонить
            </button>
            <button
              type="button"
              onClick={onApprove}
              className="flex flex-1 sm:flex-none items-center justify-center gap-[0.5rem] rounded-[10px] border-0 bg-[#10B981] px-[1rem] sm:px-[1.25rem] py-[0.625rem] sm:py-[0.75rem] text-[0.875rem] sm:text-[0.938rem] font-medium text-white hover:bg-[#059669] cursor-pointer"
            >
              <CheckIcon color="#fff" />
              Одобрить
            </button>
          </div>
        )}
      </div>
    </article>
  )
}

RequestCard.propTypes = {
  number: PropTypes.number.isRequired,
  projectName: PropTypes.string.isRequired,
  institution: PropTypes.string.isRequired,
  participants: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, code: PropTypes.string })
  ).isRequired,
  supervisors: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      position: PropTypes.string,
      phone: PropTypes.string,
    })
  ).isRequired,
  equipment: PropTypes.string.isRequired,
  submittedAt: PropTypes.string.isRequired,
  status: PropTypes.oneOf(Object.values(ORGANIZER_REQUEST_STATUSES)).isRequired,
  onApprove: PropTypes.func,
  onReject: PropTypes.func,
}

export default RequestCard
